import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { QreVarianceExplainService } from './qre-variance-explain.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-qre-variance-explain',
    templateUrl: './qre-variance-explain.component.html',
    styleUrls: ['./qre-variance-explain.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class QreVarianceExplainComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  modalTitle = 'View YoY & Sign Off';
  entityDisplayLine = '';
  localacctdesc_new = '';
  reclassTotal: number | string = 0;
  sys_acc_dropdown_data: string[] = [];
  templatedata: string[] = [];
  reclassArray: any[] = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private qreVarianceExplainService: QreVarianceExplainService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit() {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init() {
    this.buildEntityDisplay();
    this.loadData();
  }

  private buildEntityDisplay() {
    const r = this.rowData;
    const leName = r.LE_NAME ?? r.le_name ?? '';
    const cyLeid = r.CY_LEID ?? r.cy_leid ?? r.LEID ?? r.leid ?? '';
    const cyTaxCode = r.CY_TAX_CODE ?? r.cy_tax_code ?? '';
    const cyReportingPeriod = r.CY_REPORTING_PERIOD ?? r.cy_reporting_period ?? '';
    this.entityDisplayLine = `${leName} : ${cyLeid} - ${cyTaxCode} - ${cyReportingPeriod}`;
    this.cdr.detectChanges();
  }

  private getLoadParams(): Record<string, any> {
    const g = this.globalService?.globalParams || {};
    const f = this.filterParams || {};
    return {
      tax_year: this.rowData.CY_TAX_YEAR ?? this.rowData.TAX_YEAR ?? f.tax_year ?? g.tax_year,
      scenario: this.rowData.SCENARIO ?? f.scenario ?? g.scenario,
      jcd_key: this.rowData.JCD_KEY ?? g.jcd_key ?? f.jcd_key,
      ...f,
      ...g
    };
  }

  private loadData() {
    this.showFormLoader = true;
    this.cdr.detectChanges();

    const baseUrl = this.GENERAL_CONFIG.base_url;
    const commonParams = this.getLoadParams();
    const sysAcctParams = { ...commonParams, chart_type: this.rowData.CHART_TYPE ?? this.rowData.chart_type };
    const templateParams = { ...commonParams };
    const existingParams = { ...commonParams, cc_key: this.rowData.COMBINATION_KEY ?? this.rowData.combination_key };

    const load31 = this.qreVarianceExplainService.loadData(baseUrl, '31jsr3', sysAcctParams);
    const loadG9 = this.qreVarianceExplainService.loadData(baseUrl, 'g9d8eo', templateParams);
    const loadU1 = this.qreVarianceExplainService.loadData(baseUrl, 'u1yqe7', existingParams);

    let pending = 3;
    const done = () => {
      pending--;
      if (pending <= 0) {
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    };

    load31.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        if (res?.errorMessage && res.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(res.errorMessage, 'gtw-snackbar--red');
        } else if (res?.jsonObject) {
          this.sys_acc_dropdown_data = [...new Set((res.jsonObject || []).map((o: any) => o.SYSTEM_ACCT_DESC).filter(Boolean))] as string[];
        }
        done();
      },
      () => done()
    );

    loadG9.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        if (res?.errorMessage && res.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(res.errorMessage, 'gtw-snackbar--red');
        } else if (res?.jsonObject) {
          this.templatedata = [...new Set((res.jsonObject || []).map((o: any) => o.STATE_NAME).filter(Boolean))] as string[];
        }
        done();
      },
      () => done()
    );

    loadU1.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        if (res?.errorMessage && res.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(res.errorMessage, 'gtw-snackbar--red');
        } else if (res?.jsonObject) {
          this.reclassArray = res.jsonObject || [];
        }
        done();
      },
      () => done()
    );
  }

  save() {
    if (this.crudLoading || this.isSaveClicked) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    const g = this.globalService?.globalParams || {};
    if (this.reclassTotal !== 0 && this.reclassTotal !== '0') {
      this.gtwSnackbarService.openSnackBar('Please correct the errors below', 'gtw-snackbar--red');
      return;
    }
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const payload: any = {
      tax_year: g.tax_year,
      scenario: g.scenario,
      jcd_key: g.jcd_key,
      LEID: this.rowData.LEID ?? this.rowData.CY_LEID,
      CDR_NO: this.rowData.CDR_NO ?? this.rowData.CDR,
      Reporting_period: this.rowData.REPORTING_PERIOD ?? this.rowData.CY_REPORTING_PERIOD,
      me_code: this.rowData.ME_CODE,
      adj_desc_new: this.localacctdesc_new,
      type: 'I'
    };
    const sendobj = [payload];

    this.qreVarianceExplainService.saveData(
      this.GENERAL_CONFIG.base_url,
      'usumyc',
      sendobj,
      sendobj,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        if (result?.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else if (result?.callSuccess !== '1') {
          this.gtwSnackbarService.openSnackBar(result?.errorMessage || 'Error saving data', 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('TBBS Reclass has been successfully Added', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    this.isInitialized = false;
    this.showFormLoader = true;
    this.tryInit();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
